import React, {
  useEffect,
  useRef,
  Suspense,
  useCallback,
  useState,
} from "react";
import { Canvas } from "@react-three/fiber";
import { OrthographicCamera, Environment } from "@react-three/drei";
import { Loader } from "@react-three/drei";
import ReactAudioPlayer from "react-audio-player";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMicVAD } from "@ricky0123/vad-react";
import { useAppStore } from "./store";
import { useTalk, useChatMessages } from "./services/api";
import { Avatar } from "./components/Avatar";
import { createAnimation } from "./utils/animation";
import { config } from "./config";
import { Chat } from "./components/Chat";
import { Message } from "./types";
import { LanguageSelector } from "./components/UI/LanguageSelector";
import { createWavBlob } from "./utils/audio";
import { useChatParams, updateUrlParameter } from "./hooks/useChatParams";
import { getUrlParameter } from "./utils/url";
import "./App.css";

// Wrapper per i log che limita l'output in produzione
const logInDev = (...args: any[]) => {
  if (config.DEBUG) {
    console.log(...args);
  }
};

const App: React.FC = () => {
  // Query Client per invalidare queries
  const queryClient = useQueryClient();
  // Store Zustand
  const {
    audio: { source: audioSource, playing, loading },
    setAudioSource,
    setPlaying,
    setLoading,
    avatar: {
      speak,
      clips,
      morphTargetDictionaryBody,
      morphTargetDictionaryLowerTeeth,
    },
    setSpeak,
    setClips,
    setMorphTargetDictionaryBody,
    setMorphTargetDictionaryLowerTeeth,
    recording: { audio, endRecording },
    setAudio: setRecordingAudio,
    setEndRecording,
  } = useAppStore();

  // Controllo parametri dall'URL (hook reattivo)
  const {
    tenantId,
    chatId: urlChatId,
    locale,
    hasRequiredParams,
  } = useChatParams();

  // Stato locale per chatId - leggi da localStorage o URL
  const [currentChatId, setCurrentChatId] = useState<string | null>(() => {
    const stored = localStorage.getItem("chatId");
    console.log("🔍 Initializing chatId state:", {
      urlChatId,
      stored,
      final: urlChatId || stored || null,
    });
    return urlChatId || stored || null;
  });

  // Stato locale per la lingua selezionata
  // Priorità: URL (se esplicito) > localStorage > default "it"
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const storedLang = localStorage.getItem("selectedLanguage");
    const urlHasLocale = new URLSearchParams(window.location.search).has(
      "locale"
    );
    // Se l'URL ha un parametro locale esplicito, usalo (ha la priorità)
    if (urlHasLocale && locale) {
      return locale;
    }
    // Altrimenti usa il localStorage o il default
    return storedLang || "it";
  });

  // Stato locale per messaggi ottimistici (usato quando non c'è ancora chatId)
  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);

  // Query per fetchare i messaggi della chat
  const chatMessagesQuery = useChatMessages(currentChatId, !!currentChatId);
  const { data: chatData } = useQuery(chatMessagesQuery);

  // Prepara i messaggi dal backend (già nel formato corretto)
  const backendMessages: Message[] = chatData?.messages || [];

  // Mostra i messaggi corretti in base allo stato:
  // - Se non c'è chatId: usa i messaggi ottimistici locali
  // - Se c'è chatId ma i dati dal backend sono vuoti: mantieni i messaggi ottimistici (evita flash di messaggi vuoti)
  // - Se c'è chatId e ci sono dati dal backend: usa i dati dal backend
  const messages = currentChatId
    ? backendMessages.length > 0
      ? backendMessages
      : optimisticMessages
    : optimisticMessages;

  // Hooks personalizzati - using mutateAsync for Promise-based calls
  const { mutateAsync: talkMutate } = useTalk();

  // VAD State
  const [vadActive, setVadActive] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Implementazione VAD diretta
  const vad = useMicVAD({
    startOnLoad: false,
    onSpeechStart: () => {
      console.log("Speech started");
      setRecordingAudio(null);
      setEndRecording(false);
      setUserSpeaking(true);
    },
    onSpeechEnd: async (audio: Float32Array) => {
      console.log("Speech ended, audio length:", audio.length);
      const blob = createWavBlob(audio, 16000);
      console.log("Created WAV blob, size:", blob.size, "bytes");
      setRecordingAudio(blob);
      setEndRecording(true);
      setUserSpeaking(false);
    },
    positiveSpeechThreshold: 0.6,
    negativeSpeechThreshold: 0.8,
    onnxWASMBasePath:
      "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/",
    baseAssetPath:
      "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.27/dist/",
  });

  // Refs
  const audioPlayer = useRef<ReactAudioPlayer>(null);
  const speechCalledRef = useRef(false);
  const recordingProcessedRef = useRef<Blob | null>(null);
  const stopAnimationsRef = useRef<(() => void) | null>(null);
  const audioUnlockedRef = useRef(false);

  // Gestione pausa/resume VAD durante riproduzione audio
  useEffect(() => {
    if (!vad) return;

    if (playing) {
      // Metti in pausa il VAD quando l'avatar sta parlando
      console.log("Avatar playing, pausing VAD");
      vad.pause();
    } else if (vadActive && !playing && !isMuted) {
      // Riattiva solo se non è mutato
      vad.start();
    }
  }, [playing, vadActive, isMuted]);

  // Log dei parametri dall'URL
  useEffect(() => {
    if (tenantId) {
      logInDev("Parametri trovati nell'URL:", {
        tenantId,
        chatId: urlChatId,
        locale,
      });
    }
  }, [tenantId, urlChatId, locale]);

  // Sincronizza la lingua con l'URL quando cambia il parametro locale
  useEffect(() => {
    if (locale && locale !== selectedLanguage) {
      console.log("Syncing language from URL:", locale);
      setSelectedLanguage(locale);
      localStorage.setItem("selectedLanguage", locale);
    }
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ascolta i cambiamenti dell'URL (navigazione avanti/indietro)
  useEffect(() => {
    const handlePopState = () => {
      const newLocale = getUrlParameter("locale") || "it";
      if (newLocale !== selectedLanguage) {
        console.log("URL changed (popstate), syncing language:", newLocale);
        setSelectedLanguage(newLocale);
        localStorage.setItem("selectedLanguage", newLocale);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectedLanguage]);

  // Gestione del messaggio di benvenuto sulla prima interazione utente
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!localStorage.getItem("welcomePlayed")) {
        console.log("Playing welcome message on first interaction");
        localStorage.setItem("welcomePlayed", "true");
        setSpeak(true);

        document.removeEventListener("click", handleFirstInteraction);
      }
    };

    // Aggiungi listener per la prima interazione
    document.addEventListener("click", handleFirstInteraction);

    return () => {
      document.removeEventListener("click", handleFirstInteraction);
    };
  }, [setSpeak]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pulisci i messaggi ottimistici quando arrivano i dati dal backend
  useEffect(() => {
    if (
      currentChatId &&
      backendMessages.length > 0 &&
      optimisticMessages.length > 0
    ) {
      console.log("Backend messages received, clearing optimistic messages");
      setOptimisticMessages([]);
    }
  }, [currentChatId, backendMessages.length, optimisticMessages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funzioni di utilità
  const resetAudioState = useCallback(() => {
    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);
    setEndRecording(false);
    setRecordingAudio(null);
    setLoading(false);
  }, [
    setAudioSource,
    setSpeak,
    setPlaying,
    setEndRecording,
    setRecordingAudio,
    setLoading,
  ]);

  const handleApiError = useCallback((error: any) => {
    if (
      error.code === "ECONNREFUSED" ||
      error.message?.includes("Network Error")
    ) {
      alert(
        "Impossibile connettersi al server. Verifica che il server backend sia in esecuzione su localhost:3001"
      );
    } else {
      alert(`Errore nella comunicazione con il server: ${error.message}`);
    }
  }, []);

  // Funzione per chiamare /talk_old
  const makeSpeech = useCallback(
    async (text: string, audio?: Blob) => {
      if (!hasRequiredParams) {
        console.error("Parametri richiesti mancanti");
        return;
      }

      setLoading(true);
      try {
        const response = await talkMutate({
          text,
          audio,
          tenantId: tenantId!,
          chatId: currentChatId || undefined,
          locale: selectedLanguage,
        });

        // Se la risposta contiene un nuovo chatId, salvalo nel localStorage e nello stato
        if (response?.chatId && !currentChatId) {
          console.log("New chatId received:", response.chatId);
          setCurrentChatId(response.chatId);
          localStorage.setItem("chatId", response.chatId);
        }

        if (response && Object.keys(response).length !== 0) {
          const { blendData, filename } = response;

          console.log("makeSpeech - Creating animation clips");
          console.log("blendData:", blendData);
          console.log("morphTargetDictionaryBody:", morphTargetDictionaryBody);
          console.log(
            "morphTargetDictionaryLowerTeeth:",
            morphTargetDictionaryLowerTeeth
          );

          const newClips = [
            createAnimation(blendData, morphTargetDictionaryBody, "HG_Body"),
            createAnimation(
              blendData,
              morphTargetDictionaryLowerTeeth,
              "HG_TeethLower"
            ),
          ].filter((clip) => clip !== null);

          console.log("makeSpeech - Created clips:", newClips);

          const fullFilename = config.API_URL + filename;
          setClips(newClips);
          setAudioSource(fullFilename);
          setLoading(false);

          // Riproduci l'audio immediatamente dal contesto dell'interazione utente
          setTimeout(async () => {
            const audioElement = audioPlayer.current?.audioEl.current;
            if (audioElement) {
              try {
                audioElement.load();

                const playPromise = audioElement.play();
                if (playPromise !== undefined) {
                  await playPromise;
                  console.log(
                    "Audio started playing successfully - setting playing to true"
                  );
                  setPlaying(true);
                }
              } catch (error) {
                console.error("Error playing audio:", error);
                if (
                  error instanceof Error &&
                  error.name === "NotAllowedError"
                ) {
                  console.log(
                    "Autoplay blocked - user needs to interact first"
                  );
                }
              }
            }
          }, 100);
        } else {
          alert("Error while playing audio, try again");
          resetAudioState();
        }
      } catch (error) {
        console.error("Error in makeSpeech:", error);
        handleApiError(error);
        resetAudioState();
      }
    },
    [
      talkMutate,
      tenantId,
      currentChatId,
      selectedLanguage,
      morphTargetDictionaryBody,
      morphTargetDictionaryLowerTeeth,
      setClips,
      setAudioSource,
      setLoading,
      resetAudioState,
      handleApiError,
    ]
  );

  // Funzione per chiamare /talk
  const makeSpeechNew = useCallback(
    async ({ text, audio }: { text: string; audio: Blob | null }) => {
      console.log("🎯 makeSpeechNew called with:", {
        text: text || "[AUDIO]",
        hasAudio: !!audio,
        currentChatId,
        tenantId,
        locale,
      });

      // ID temporaneo per il messaggio dell'utente
      const tempUserId = `temp-user-${Date.now()}`;

      // Aggiungi il messaggio dell'utente ottimisticamente
      if (!currentChatId) {
        // Senza chatId, usa lo stato locale
        const userMessage: Message = {
          id: tempUserId,
          role: "user",
          content: text || "🎤 Messaggio vocale",
          created_at: new Date(),
        };
        setOptimisticMessages((prev) => [...prev, userMessage]);
      } else {
        // Con chatId, usa l'optimistic update di React Query
        queryClient.setQueryData(
          ["chatMessages", currentChatId],
          (old: any) => {
            if (!old) return old;
            const userMessage: Message = {
              id: tempUserId,
              role: "user",
              content: text || "🎤 Messaggio vocale",
              created_at: new Date(),
              chat_id: parseInt(currentChatId),
            };
            return {
              ...old,
              messages: [...(old.messages || []), userMessage],
            };
          }
        );
      }

      const body: any = {
        text,
        tenantId: tenantId!,
        chatId: currentChatId || undefined,
        locale: selectedLanguage,
      };
      if (audio) {
        body.audio = audio;
      }

      try {
        const response = await talkMutate(body);
        console.log("📩 Full response from backend:", response);
        console.log("📩 Response chatId:", response?.chatId);
        console.log("📩 Current chatId:", currentChatId);

        const newChatId = response?.chatId;

        // Se abbiamo ricevuto un nuovo chatId
        if (newChatId && !currentChatId) {
          console.log("✅ New chatId received:", newChatId);

          // Aggiungi il messaggio dell'assistente ai messaggi ottimistici se disponibile
          if (response.text) {
            const assistantMessage: Message = {
              id: `temp-assistant-${Date.now()}`,
              role: "assistant",
              content: response.text,
              created_at: new Date(),
            };
            setOptimisticMessages((prev) => [...prev, assistantMessage]);
          }

          // Imposta il nuovo chatId - questo abiliterà automaticamente la query
          console.log("💾 Saving chatId to state and localStorage:", newChatId);
          setCurrentChatId(newChatId);
          localStorage.setItem("chatId", newChatId);
          console.log(
            "💾 localStorage chatId after save:",
            localStorage.getItem("chatId")
          );

          // Invalida la query con il nuovo chatId per fetchare i messaggi reali
          // I messaggi ottimistici verranno puliti automaticamente dal useEffect quando arrivano i dati
          console.log("🔄 Invalidating query for chatId:", newChatId);
          queryClient.invalidateQueries({
            queryKey: ["chatMessages", newChatId],
          });
        } else if (currentChatId) {
          // Se abbiamo già un chatId, invalida la query esistente per ottenere i messaggi aggiornati
          console.log(
            "🔄 Invalidating query for existing chatId:",
            currentChatId
          );
          queryClient.invalidateQueries({
            queryKey: ["chatMessages", currentChatId],
          });
        } else {
          console.warn(
            "⚠️ No chatId received from backend and no existing chatId"
          );
          console.warn(
            "⚠️ newChatId:",
            newChatId,
            "currentChatId:",
            currentChatId
          );
        }

        if (response && Object.keys(response).length !== 0) {
          const { blendData, filename } = response;
          const newClips = [
            createAnimation(blendData, morphTargetDictionaryBody, "HG_Body"),
            createAnimation(
              blendData,
              morphTargetDictionaryLowerTeeth,
              "HG_TeethLower"
            ),
          ].filter((clip) => clip !== null);

          const fullFilename = config.API_URL + filename;
          setClips(newClips);
          setAudioSource(fullFilename);
          setLoading(false);

          setTimeout(async () => {
            const audioElement = audioPlayer.current?.audioEl.current;
            if (audioElement) {
              try {
                audioElement.load();
                const playPromise = audioElement.play();
                if (playPromise !== undefined) {
                  await playPromise;
                  console.log(
                    "Audio started playing successfully - setting playing to true"
                  );
                  setPlaying(true);
                }
              } catch (error) {
                console.error("Error playing audio:", error);
                if (
                  error instanceof Error &&
                  error.name === "NotAllowedError"
                ) {
                  console.log(
                    "Autoplay blocked - user needs to interact first"
                  );
                }
              }
            }
          }, 100);
        } else {
          alert("Error while recording audio, try again");
          resetAudioState();
        }
      } catch (error) {
        console.error("Error in makeSpeechNew:", error);
        handleApiError(error);
        resetAudioState();
      }
    },
    [
      talkMutate,
      tenantId,
      currentChatId,
      selectedLanguage,
      morphTargetDictionaryBody,
      morphTargetDictionaryLowerTeeth,
      setClips,
      setAudioSource,
      setLoading,
      resetAudioState,
      handleApiError,
      queryClient,
      setOptimisticMessages,
    ]
  );

  useEffect(() => {
    if (speak === false) {
      speechCalledRef.current = false;
      return;
    }
    // Evita chiamate multiple
    if (speechCalledRef.current) return;

    // Riproduci il messaggio di benvenuto
    makeSpeech("Ciao!", null as any);
    speechCalledRef.current = true;
  }, [speak]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gestione della registrazione audio
  useEffect(() => {
    if (endRecording === false) {
      recordingProcessedRef.current = null;
      return;
    }

    // Evita di processare lo stesso audio più volte
    if (!audio || recordingProcessedRef.current === audio) return;
    recordingProcessedRef.current = audio;

    makeSpeechNew({ text: "", audio });
  }, [endRecording, audio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sblocca l'audio per iOS Safari - deve essere chiamata durante un'interazione utente
  const unlockAudioForIOS = useCallback(() => {
    if (audioUnlockedRef.current) return;

    try {
      // Crea un AudioContext (questo è ciò che Safari richiede di sbloccare)
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        const audioContext = new AudioContextClass();

        // Crea un buffer vuoto e riproducilo
        const buffer = audioContext.createBuffer(1, 1, 22050);
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.start(0);

        if (audioContext.state === "suspended") {
          audioContext.resume();
        }

        console.log("🔓 AudioContext unlocked for iOS Safari");
        audioUnlockedRef.current = true;
      }
    } catch (error) {
      console.log("AudioContext unlock error:", error);
    }
  }, []);

  // Funzione per gestire il click del pulsante principale
  const handleSpeakButtonClick = async () => {
    // 🔓 Sblocca l'audio iOS al click
    unlockAudioForIOS();

    // Se sta già parlando, ferma tutto
    if (playing || loading) {
      stopCurrentSpeech();
      return;
    }

    // Attiva lo speak
    setSpeak(true);
  };

  // Funzione per gestire il toggle del VAD tramite bottone
  const handleRecording = async () => {
    // 🔓 Sblocca l'audio iOS al click del pulsante VAD
    unlockAudioForIOS();

    if (!vad) return;

    if (vadActive) {
      // Disattiva il VAD completamente
      console.log("Stopping VAD completely");
      vad.pause();
      setVadActive(false);
      setIsMuted(false); // Reset mute quando si disattiva VAD
      setUserSpeaking(false);
    } else {
      // Attiva il VAD
      if (playing || loading) {
        stopCurrentSpeech();
      }
      console.log("Starting VAD");
      setIsMuted(false); // Assicurati che non sia mutato quando si attiva
      vad.start();
      setVadActive(true);
    }
  };

  // Funzione per gestire il mute/unmute del microfono
  const handleMuteToggle = () => {
    if (!vad || !vadActive) return;

    if (isMuted) {
      // Unmute - riattiva il VAD
      console.log("Unmuting VAD");
      vad.start();
      setIsMuted(false);
    } else {
      // Mute - metti in pausa il VAD ma mantieni vadActive true
      console.log("Muting VAD");
      vad.pause();
      setIsMuted(true);
      setUserSpeaking(false);
    }
  };

  // Funzione per gestire l'invio di messaggi dalla chat
  const handleSendMessage = (message: string) => {
    // 🔓 Sblocca l'audio iOS al click
    unlockAudioForIOS();
    // Invia il messaggio direttamente al backend
    makeSpeechNew({ text: message, audio: null });
  };

  // Funzione per gestire il cambio di lingua
  const handleLanguageChange = (languageCode: string) => {
    console.log("Language changed to:", languageCode);
    setSelectedLanguage(languageCode);
    localStorage.setItem("selectedLanguage", languageCode);
    // Aggiorna anche l'URL
    updateUrlParameter("locale", languageCode);
  };

  const playerEnded = () => {
    console.log("Audio ended naturally - resetting states");

    // Ferma le animazioni dell'avatar
    if (stopAnimationsRef.current) {
      stopAnimationsRef.current();
    }

    // Reset degli stati
    setPlaying(false);
    setLoading(false);
    setSpeak(false); // Reset anche dello stato speak
    setClips([]);
    setAudioSource("");

    // Reset dei ref per evitare conflitti
    speechCalledRef.current = false;
    recordingProcessedRef.current = null;

    console.log("Audio ended and states reset");
  };

  // Funzione per fermare l'audio e le animazioni
  const stopCurrentSpeech = () => {
    console.log("Stopping current speech...");

    // Ferma l'audio
    const audioElement = audioPlayer.current?.audioEl.current;
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    // Ferma le animazioni dell'avatar
    if (stopAnimationsRef.current) {
      stopAnimationsRef.current();
    }

    // Reset degli stati
    setPlaying(false);
    setLoading(false);
    setSpeak(false); // Reset anche dello stato speak
    setClips([]);
    setAudioSource("");

    // Reset dei ref per evitare conflitti
    speechCalledRef.current = false;
    recordingProcessedRef.current = null;
  };

  const isMobile = window.innerWidth < 768;

  return (
    <div className="app-container">
      {/* Logo floating in alto a sinistra */}
      <div
        onClick={() => localStorage.removeItem("chatId")}
        className="floating-logo"
      >
        <img
          src="/images/ui/musea_logo.png"
          alt="Musea Logo"
          className="floating-logo-img"
        />
      </div>
      {/* Canvas 3D - frameloop="demand" riduce il consumo quando idle */}
      <Canvas
        dpr={[1, 1.5]}
        frameloop="demand"
        onCreated={(ctx) => {
          console.log("Canvas created");
        }}
        className="avatar-container"
      >
        <OrthographicCamera
          makeDefault
          zoom={isMobile ? 800 : 900}
          position={[0, 0.7, 1]}
        />
        <Suspense fallback={null}>
          <Environment
            background={false}
            files="/images/photo_studio_loft_hall_1k.hdr"
          />
        </Suspense>
        <Suspense fallback={null}>
          <Avatar
            avatar_url="/try2.glb"
            speak={speak}
            setSpeak={setSpeak}
            text=""
            setAudioSource={setAudioSource}
            playing={playing}
            setPlaying={setPlaying}
            audio={audio}
            setAudio={setRecordingAudio}
            endRecording={endRecording}
            setEndRecording={setEndRecording}
            loading={loading}
            setLoading={setLoading}
            clips={clips}
            setClips={setClips}
            tenantId={tenantId || ""}
            audioSource={audioSource}
            audioPlayer={audioPlayer}
            unlockAudioForIOS={() => {}}
            initAudioContext={async () => {}}
            setMorphTargetDictionaryBody={setMorphTargetDictionaryBody}
            setMorphTargetDictionaryLowerTeeth={
              setMorphTargetDictionaryLowerTeeth
            }
            morphTargetDictionaryBody={morphTargetDictionaryBody}
            morphTargetDictionaryLowerTeeth={morphTargetDictionaryLowerTeeth}
            stopAnimationsRef={stopAnimationsRef}
          />
        </Suspense>
      </Canvas>
      {/* Link Pura Digital */}
      <a
        href="https://www.puradigital.it"
        target="_blank"
        rel="noopener noreferrer"
        className="pura-link"
      >
        Pura Digital{" "}
        <img
          src="/images/ui/pura_logo.png"
          alt="Pura Digital"
          className="pura-logo"
        />
      </a>
      <a
        href="https://www.agilae.it/"
        target="_blank"
        rel="noopener noreferrer"
        className="agilae-link"
      >
        {" "}
        <img
          src="/images/ui/logo_agilae.png"
          alt="Agilae"
          className="agilae-logo"
        />
      </a>

      {/* Language Selector */}
      <LanguageSelector
        selectedLanguage={selectedLanguage}
        onLanguageChange={handleLanguageChange}
      />

      {/* Messaggio parametri mancanti */}
      {!hasRequiredParams && (
        <div className="token-message">
          <p>
            Per iniziare, aggiungi il tuo <b>tenantId</b> e <b>locale</b>{" "}
            all'URL.
          </p>
          <p>
            Esempio: http://localhost:3000/?tenantId=<b>ilTuoTenantId</b>
            &locale=<b>it-IT</b>
          </p>
        </div>
      )}

      {/* Chat wrapper con indicatore di registrazione */}
      <div className="chat-wrapper">
        {userSpeaking && <div className="recording-indicator" />}
        <Chat
          messages={messages}
          onSendMessage={handleSendMessage}
          onSpeak={() => handleSpeakButtonClick()}
          onRecord={() => handleRecording()}
          onMuteToggle={() => handleMuteToggle()}
          isRecording={userSpeaking}
          vadListening={vadActive}
          isMuted={isMuted}
          loading={loading}
          playing={playing}
          hasTenantId={hasRequiredParams}
        />
      </div>

      {/* Player audio nascosto */}
      <ReactAudioPlayer
        src={audioSource || ""}
        ref={audioPlayer}
        onEnded={playerEnded}
        style={{ display: "none" }}
      />

      <Loader dataInterpolation={(p) => `Loading... please wait`} />
    </div>
  );
};

export default App;
